import type { LtvResult, RetentionGroupBy, RoasBreakdown, RoasBy, RoasMode, RoasOverview } from '@growth/client';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { LineChart } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { AssumptionsNote, AttributionToggle, BasisToggle, EstimatedBadge, fmtRoas, useMoneyParams } from '../components/money.tsx';
import { AttributionBadge } from '../components/ua.tsx';
import { Card, ChartCard, DataTable, Empty, ErrorBox, SampleBadge, StatTile } from '../components/ui.tsx';
import { useGlobalFilters } from '../filters.ts';
import { fmtDay, fmtInt, fmtMoney, fmtMoneyRange } from '../format.ts';
import { MIN_SAMPLE } from '../metrics.ts';
import { useApi } from '../session.tsx';

const GROUPS: { value: RetentionGroupBy; label: string }[] = [
  { value: 'none', label: 'Todas' },
  { value: 'install_date', label: 'Día de instalación' },
  { value: 'country', label: 'País' },
  { value: 'network', label: 'Fuente' },
  { value: 'campaign', label: 'Campaña' },
  { value: 'creative', label: 'Creatividad' },
  { value: 'app_version', label: 'Versión' },
];

const BREAKDOWNS: { by: RoasBy; label: string }[] = [
  { by: 'country', label: 'País' },
  { by: 'network', label: 'Fuente' },
  { by: 'campaign', label: 'Campaña' },
  { by: 'ad_group', label: 'Grupo' },
  { by: 'creative', label: 'Creatividad' },
  { by: 'concept', label: 'Concepto' },
];

const MODE_LABEL: Record<string, string> = { shared: 'Compartida', blended: 'Mezclada' };

function paybackText(m: RoasMode | null | undefined): string {
  if (!m) return '—';
  if (m.payback.observed_day !== null) return `Día ${m.payback.observed_day}`;
  if (m.payback.status === 'no_spend') return 'Sin gasto';
  return `No en ${m.observed_until ?? 0} días`;
}

function BreakdownCard({ gameId, params, money }: { gameId: string; params: Record<string, unknown>; money: ReturnType<typeof useMoneyParams> }) {
  const [by, setBy] = useState<RoasBy>('country');
  const [day, setDay] = useState(7);
  const key = `roas-bd:${gameId}:${by}:${day}:${JSON.stringify(params)}:${money.basis}:${money.attribution}`;
  const r = useApi<RoasBreakdown>(key, (api, s) => api.roasBreakdown(gameId, by, { ...params, day, basis: money.basis, attribution: money.attribution }, s));
  const d = r.data;
  const ccy = d?.currency ?? 'EUR';
  return (
    <Card
      title={`ROAS D${day} por ${BREAKDOWNS.find((b) => b.by === by)!.label.toLowerCase()}`}
      subtitle={d ? `Solo días de instalación maduros para D${day} (${fmtDay(d.mature_from)} – ${fmtDay(d.mature_to)}); gasto de esos mismos días.` : undefined}
      actions={
        <div className="row">
          <div className="segmented" role="group" aria-label="Día">
            {[1, 3, 7, 14, 30].map((n) => (
              <button key={n} type="button" aria-pressed={day === n} onClick={() => setDay(n)}>
                D{n}
              </button>
            ))}
          </div>
          <div className="segmented" role="group" aria-label="Desglose">
            {BREAKDOWNS.map((b) => (
              <button key={b.by} type="button" aria-pressed={by === b.by} onClick={() => setBy(b.by)}>
                {b.label}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {r.error && <ErrorBox error={r.error} onRetry={r.reload} />}
      {d?.notes.map((n) => (
        <p key={n} className="small muted" style={{ margin: '0 0 6px' }}>
          {n.startsWith('No install day') ? `Ningún día de instalación del rango tiene ${day} días todavía.` : n.startsWith('Revenue per creative') ? 'Los ingresos por creatividad necesitan el creative_id en el SDK: las campañas de aplicaciones no lo pasan, así que muchas filas solo tienen gasto.' : n.startsWith('Players without network') ? 'Los jugadores sin atribución a una red aparecen como «organic» (sin gasto).' : n.startsWith('Spend per country') ? 'Gasto por país del informe de ubicación; ingresos de los jugadores instalados en ese país.' : n}
        </p>
      ))}
      <div className={`loading-frame${r.loading && d ? ' stale' : ''}`}>
        {d && d.rows.length === 0 ? (
          <Empty>Sin datos para este desglose.</Empty>
        ) : (
          <DataTable
            data={{
              columns: [
                { label: BREAKDOWNS.find((b) => b.by === by)!.label },
                { label: 'Atribución' },
                { label: 'Gasto', numeric: true },
                { label: 'Jugadores', numeric: true },
                { label: `Ingresos D${day}`, numeric: true },
                { label: `ROAS D${day}`, numeric: true },
                { label: `LTV D${day}`, numeric: true },
                ...(by === 'campaign' ? [{ label: 'Valor Google Ads', numeric: true }, { label: 'ROAS según la red', numeric: true }] : []),
              ],
              rows: (d?.rows ?? []).map((x) => [
                by === 'creative' && x.attribution !== 'none' ? <Link to={`/g/${gameId}/creatives/${encodeURIComponent(x.key)}`}>{x.name ?? x.key}</Link> : (x.name ?? x.key),
                x.attribution === 'none' ? <span className="muted" title={x.note}>sin jugadores</span> : <AttributionBadge kind={x.attribution} />,
                fmtMoney(x.spend, ccy),
                <>
                  {fmtInt(x.installs)} {x.installs > 0 && x.installs < 30 ? <SampleBadge sample={x.sample} /> : null}
                </>,
                fmtMoney(x.revenue, ccy),
                fmtRoas(x.roas),
                fmtMoney(x.ltv, ccy, 3),
                ...(by === 'campaign' ? [fmtMoney(x.network_value ?? null, ccy), fmtRoas(x.network_roas ?? null)] : []),
              ]),
            }}
          />
        )}
      </div>
    </Card>
  );
}

export function LtvRoasPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const money = useMoneyParams();
  const [group, setGroup] = useState<RetentionGroupBy>('none');
  const params = filters.params as Record<string, unknown>;
  const roas = useApi<RoasOverview>(`roas:${gameId}:${filters.key}:${money.basis}:${money.attribution}`, (api, s) => api.roas(gameId, { ...filters.params, basis: money.basis, attribution: money.attribution }, s));
  const ltv = useApi<LtvResult>(`ltv:${gameId}:${filters.key}:${money.basis}:${group}`, (api, s) => api.ltv(gameId, { ...filters.params, basis: money.basis, group_by: group }, s));
  const o = roas.data;
  const l = ltv.data;
  const ccy = o?.currency ?? l?.currency ?? 'EUR';
  const primary = o ? (o.modes[o.attribution] ?? o.modes.blended) : null;
  const day = (m: RoasMode | null | undefined, n: number) => m?.days.find((x) => x.day === n);
  const curveModes = o ? ([o.modes.shared, o.modes.blended].filter(Boolean) as RoasMode[]) : [];
  const curveX = Array.from({ length: Math.max(0, ...curveModes.map((m) => m.curve.length)) }, (_, i) => i);
  const est = primary?.estimated ?? null;
  const fragile = l?.groups.some((g) => g.days.some((c) => c.iap_fragile)) ?? false;

  return (
    <>
      <PageHeader
        title="LTV y ROAS"
        sub="Cohortes de instalación: ingresos observados hasta el día N ÷ gasto de esos días. Solo cohortes maduras; las proyecciones van aparte y marcadas como ESTIMADO."
        right={
          <>
            <AttributionToggle value={money.attribution} onChange={money.setAttribution} />
            <BasisToggle value={money.basis} onChange={money.setBasis} />
          </>
        }
      />
      <FilterBar filters={filters} compare={false} environment={false} />
      {roas.error && <ErrorBox error={roas.error} onRetry={roas.reload} />}
      {o?.notes.map((n) => (
        <div key={n} className="notice">
          {n.startsWith('No ad-network spend') ? 'Sin gasto en redes en el periodo: solo se muestran ingresos (ROAS y payback necesitan gasto).' : n}
        </div>
      ))}

      <div className={`kpis loading-frame${roas.loading && o ? ' stale' : ''}`}>
        <StatTile label="CPI de la cohorte" value={fmtMoney(primary?.cpi, ccy, 3)} sub={primary ? `${fmtInt(primary.installs)} jugadores · atribución ${MODE_LABEL[primary.mode]?.toLowerCase()}` : undefined} />
        <StatTile label="LTV D7" value={fmtMoney(day(primary, 7)?.ltv, ccy, 3)} sub={`${fmtInt(day(primary, 7)?.mature_installs)} jugadores con 7 días`} />
        <StatTile label="LTV D30" value={fmtMoney(day(primary, 30)?.ltv, ccy, 3)} sub={`${fmtInt(day(primary, 30)?.mature_installs)} jugadores con 30 días`} />
        <StatTile label="ROAS D7" value={fmtRoas(day(primary, 7)?.roas)} sub={day(primary, 7)?.spend ? `gasto ${fmtMoney(day(primary, 7)?.spend, ccy)}` : 'sin gasto maduro'} />
        <StatTile label="ROAS D30" value={fmtRoas(day(primary, 30)?.roas)} sub={day(primary, 30)?.spend ? `gasto ${fmtMoney(day(primary, 30)?.spend, ccy)}` : 'sin gasto maduro'} />
        <StatTile
          label="Payback"
          value={paybackText(primary)}
          compact
          sub={est?.payback_day !== undefined && est?.payback_day !== null ? `ESTIMADO: ~día ${est.payback_day} (curva potencial, R² ${est.r2})` : 'observado: día en que ingresos ≥ gasto'}
        />
        <StatTile
          label="Valor según Google Ads"
          value={o?.network_reported.available ? fmtMoney(o.network_reported.value, ccy) : '—'}
          sub={o?.network_reported.available ? `ROAS según la red ${fmtRoas(o.network_reported.roas)} (día del clic)` : 'importa compras/ingresos de Firebase como conversiones con valor'}
        />
      </div>

      <div className="grid cols-2">
        <ChartCard
          title="ROAS acumulado por día de vida"
          subtitle={`Cada punto usa solo los días de instalación que ya tienen esa edad (cohortes distintas en cada día; se dibujan los puntos con ≥ ${MIN_SAMPLE} jugadores). 100 % = se recupera el gasto.`}
          stale={roas.loading && !!o}
          table={{
            columns: [{ label: 'Día' }, ...curveModes.flatMap((m) => [{ label: `ROAS ${MODE_LABEL[m.mode]}`, numeric: true }, { label: 'Jugadores', numeric: true }])],
            rows: curveX.map((i) => [`D${i}`, ...curveModes.flatMap((m) => [fmtRoas(m.curve[i]?.roas), fmtInt(m.curve[i]?.mature_installs)])]),
          }}
        >
          {curveX.length ? (
            <LineChart
              ariaLabel="ROAS acumulado"
              x={curveX}
              linearX
              formatX={(v) => `D${v}`}
              formatY={(v) => fmtRoas(v)}
              series={curveModes.map((m, k) => ({ key: m.mode, label: MODE_LABEL[m.mode] ?? m.mode, slot: k + 1, values: m.curve.map((p) => (p.mature_installs >= MIN_SAMPLE ? p.roas : null)) }))}
              tooltipNote={(i) => curveModes.map((m) => `${MODE_LABEL[m.mode]}: ${fmtInt(m.curve[i]?.mature_installs)} jugadores`).join(' · ')}
            />
          ) : (
            <Empty>Sin cohortes con gasto en el periodo.</Empty>
          )}
        </ChartCard>
        <ChartCard
          title="LTV acumulado (observado)"
          subtitle={`Ingresos por jugador desde la instalación, separados en anuncios y compras (puntos con ≥ ${MIN_SAMPLE} jugadores; la tabla los tiene todos).`}
          stale={ltv.loading && !!l}
          table={
            l && {
              columns: [{ label: 'Día' }, { label: 'LTV', numeric: true }, { label: 'Anuncios', numeric: true }, { label: 'Compras', numeric: true }, { label: 'Jugadores', numeric: true }],
              rows: l.curve.map((p) => [`D${p.day}`, fmtMoney(p.ltv, ccy, 4), fmtMoney(p.ltv_ad, ccy, 4), fmtMoney(p.ltv_iap, ccy, 4), fmtInt(p.mature_installs)]),
            }
          }
        >
          {l && l.curve.length ? (
            <LineChart
              ariaLabel="LTV acumulado"
              x={l.curve.map((p) => p.day)}
              linearX
              formatX={(v) => `D${v}`}
              formatY={(v) => fmtMoney(v, ccy, 2)}
              formatValue={(v) => fmtMoney(v, ccy, 4)}
              series={[
                { key: 'ltv', label: 'Total', slot: 1, values: l.curve.map((p) => (p.mature_installs >= MIN_SAMPLE ? p.ltv : null)) },
                { key: 'ad', label: 'Anuncios', slot: 2, values: l.curve.map((p) => (p.mature_installs >= MIN_SAMPLE ? p.ltv_ad : null)) },
                { key: 'iap', label: 'Compras', slot: 3, values: l.curve.map((p) => (p.mature_installs >= MIN_SAMPLE ? p.ltv_iap : null)) },
              ]}
            />
          ) : (
            <Empty>Sin instalaciones nuevas en el periodo.</Empty>
          )}
        </ChartCard>
      </div>

      <Card
        title={
          <>
            Proyección <EstimatedBadge />
          </>
        }
        subtitle="Ajuste LTV(N) = a·(N+1)^b sobre la curva observada. Solo se calcula con ≥ 5 puntos de ≥ 30 jugadores, observación ≥ D7 y R² ≥ 0,9. Nunca se mezcla con los valores observados."
      >
        {est ? (
          <>
            <DataTable
              data={{
                columns: [{ label: 'Día' }, { label: 'LTV estimado', numeric: true }, { label: 'ROAS estimado', numeric: true }],
                rows: est.values.map((v, i) => [`D${v.day}`, fmtMoney(v.ltv, ccy, 3), fmtRoas(est.roas[i]?.roas)]),
              }}
            />
            <p className="small muted" style={{ margin: '6px 0 0' }}>
              R² {est.r2} · b = {est.b} · observado hasta D{est.observed_until}. Supone que la curva mantiene su forma (sin cambios de contenido, precios o tráfico): revísalo según maduren las cohortes.
            </p>
          </>
        ) : (
          <Empty>Sin proyección: hacen falta más días observados o una curva más estable.</Empty>
        )}
      </Card>

      <Card
        title="LTV por cohorte"
        subtitle={l ? `Instalaciones del ${fmtDay(l.filters.from)} al ${fmtDay(l.filters.to)} · último día completo ${fmtDay(l.last_complete_day)} · IC 95 % de la media por jugador` : undefined}
        actions={
          <select className="input" value={group} onChange={(e) => setGroup(e.target.value as RetentionGroupBy)} aria-label="Agrupar por">
            {GROUPS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        }
      >
        {ltv.error && <ErrorBox error={ltv.error} onRetry={ltv.reload} />}
        <div className={`loading-frame${ltv.loading && l ? ' stale' : ''}`}>
          <DataTable
            data={{
              columns: [{ label: GROUPS.find((g) => g.value === group)!.label }, { label: 'Jugadores', numeric: true }, ...(l?.days ?? []).map((n) => ({ label: `D${n}`, numeric: true }))],
              rows: (l?.groups ?? []).map((g) => [
                g.group === 'all' ? 'Todas' : g.group,
                fmtInt(g.cohort_size),
                ...(l?.days ?? []).map((n) => {
                  const c = g.days.find((x) => x.day === n);
                  if (!c || c.ltv === null) return '—';
                  return (
                    <span title={`IC 95 % ${fmtMoneyRange(c.ci_low, c.ci_high, ccy)} · ${c.mature} jugadores · ${c.payers.k} pagadores${c.iap_fragile ? ' (pocos: la parte de compras es frágil)' : ''}`}>
                      {fmtMoney(c.ltv, ccy, 3)}
                      {c.iap_fragile ? '*' : ''}
                      <span className="muted small"> n={fmtInt(c.mature)}</span>
                    </span>
                  );
                }),
              ]),
            }}
          />
          {fragile && <p className="small muted" style={{ margin: '6px 0 0' }}>* menos de 10 pagadores sostienen la parte de compras: el intervalo subestima la incertidumbre.</p>}
        </div>
      </Card>

      <BreakdownCard gameId={gameId} params={params} money={money} />
      <AssumptionsNote a={o?.assumptions ?? l?.assumptions} />
    </>
  );
}
